/**
 * AddSchemaDialog Component
 *
 * Dialog for creating a new multi-field Schema.
 * Features:
 * - Schema name, displayName, description inputs
 * - Multiple field management with add/remove/reorder
 * - Drag-and-drop field reordering
 * - Import fields from JSON or existing schemas
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Download, FileCode } from 'lucide-react';
import {
  schemaApi,
  SchemaField,
  createDefaultField,
  generateSchemaName,
  isValidSchemaName,
} from '@/services/api/schemaApi';
import { AdminSchemaFieldEditor } from './AdminSchemaFieldEditor';
import { ImportFieldsDialog } from './ImportFieldsDialog';
import * as errorUtils from '@/utils/errors';

interface AddSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddSchemaDialog: React.FC<AddSchemaDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  // Form state
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<SchemaField[]>([createDefaultField()]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Reset form
  const resetForm = useCallback(() => {
    setName('');
    setDisplayName('');
    setDescription('');
    setFields([createDefaultField()]);
    setError(null);
    setShowImportDialog(false);
  }, []);

  // Handle dialog close
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        resetForm();
      }
      onOpenChange(isOpen);
    },
    [onOpenChange, resetForm]
  );

  // Auto-generate name from displayName
  const handleDisplayNameChange = useCallback((value: string) => {
    setDisplayName(value);
    setName(generateSchemaName(value));
  }, []);

  // Field management
  const handleAddField = useCallback(() => {
    setFields((prev) => [...prev, createDefaultField()]);
  }, []);

  const handleUpdateField = useCallback((index: number, updatedField: SchemaField) => {
    setFields((prev) => prev.map((f, i) => (i === index ? updatedField : f)));
  }, []);

  const handleRemoveField = useCallback((index: number) => {
    setFields((prev) => {
      if (prev.length <= 1) return prev; // Keep at least one field
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === dropIndex) return;

      setFields((prev) => {
        const newFields = [...prev];
        const [removed] = newFields.splice(dragIndex, 1);
        newFields.splice(dropIndex, 0, removed);
        return newFields;
      });

      setDragIndex(null);
      setDragOverIndex(null);
    },
    [dragIndex]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  // Import fields from ImportFieldsDialog (merge with overwrite)
  const handleImportFields = useCallback((importedFields: SchemaField[]) => {
    setFields((prevFields) => {
      const result = [...prevFields];

      for (const imported of importedFields) {
        const existingIndex = result.findIndex((f) => f.name === imported.name);
        if (existingIndex >= 0) {
          // Overwrite existing field, keep the existing ID
          result[existingIndex] = { ...imported, id: result[existingIndex].id };
        } else {
          // Add new field
          result.push(imported);
        }
      }

      return result;
    });
  }, []);

  // Validation
  const validateForm = useCallback((): string | null => {
    if (!name.trim()) {
      return 'Schema name is required';
    }

    if (!isValidSchemaName(name)) {
      return 'Schema name must be lowercase with underscores (e.g., sensor_data)';
    }

    if (!displayName.trim()) {
      return 'Display name is required';
    }

    if (!description.trim()) {
      return 'Description is required';
    }

    if (fields.length === 0) {
      return 'At least one field is required';
    }

    // Validate each field
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field.name.trim()) {
        return `Field ${i + 1} is missing a name`;
      }
      if (!/^[a-z][a-z0-9_]*$/.test(field.name)) {
        return `Field "${field.name}" must be lowercase with underscores`;
      }
    }

    // Check for duplicate field names
    const fieldNames = fields.map((f) => f.name);
    const duplicates = fieldNames.filter((name, index) => fieldNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      return `Duplicate field name: ${duplicates[0]}`;
    }

    return null;
  }, [name, displayName, description, fields]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await schemaApi.createSchema({
        name: name.trim(),
        displayName: displayName.trim(),
        description: description.trim(),
        fields,
      });

      handleOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to create schema');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, displayName, description, fields, validateForm, handleOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            Create Schema
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4 px-1">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium border-b pb-2">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                placeholder="e.g., Sensor Data"
                value={displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Technical Name *</Label>
              <Input
                id="name"
                placeholder="e.g., sensor_data"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                snake_case identifier (auto-generated from display name)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe what data this schema represents..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Fields Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-medium">
                Fields ({fields.length})
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowImportDialog(true)}
                  className="gap-1"
                >
                  <Download className="h-4 w-4" />
                  Import
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddField}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Field
                </Button>
              </div>
            </div>

            {fields.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No fields defined.</p>
                <p className="text-sm">Click "Add Field" to create your first field.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {fields.map((field, index) => (
                  <AdminSchemaFieldEditor
                    key={field.id}
                    field={field}
                    index={index}
                    onChange={(updated) => handleUpdateField(index, updated)}
                    onRemove={() => handleRemoveField(index)}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    isDragging={dragIndex === index}
                    isDragOver={dragOverIndex === index}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? 'Creating...' : 'Create Schema'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Import Fields Dialog */}
      <ImportFieldsDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        existingFields={fields}
        onImport={handleImportFields}
      />
    </Dialog>
  );
};

export default AddSchemaDialog;
